import { RaidPlanEncounterAssignmentAbility } from 'app/frontend/types/custom/RaidPlan'
import { EncounterAbilityRow } from '../../TeamPlans/hooks/useTeamPlanComputedData'
import {
  AssignmentViewMode,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getAssignmentScopeIndexes,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import {
  EncounterAssignmentEntityOverride,
  EventPlanEncounterAssignmentOverrides,
  getEncounterAssignmentEntityOverride,
} from '../components/eventActivityPlanOverrides'
import {
  EventActivityTimelineResolverIssue,
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from '../components/eventActivityTimelineResolver'

type ApplyEventActivityPlanOverridesInput = {
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>
  abilities: RaidPlanEncounterAssignmentAbility[]
  encounterRows: EncounterAbilityRow[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  encounterAssignmentOverrides?: EventPlanEncounterAssignmentOverrides
}

const toCastIds = (
  ability: RaidPlanEncounterAssignmentAbility,
  castIndexes: number[],
  assignmentGroups: number,
) =>
  castIndexes
    .map(
      (castIndex) =>
        normalizeAssignmentCast(ability.casts?.[castIndex], assignmentGroups)
          .id,
    )
    .filter((castId): castId is string => Boolean(castId))

export const applyEventActivityPlanOverrides = ({
  resolvedByScopeBoxKey,
  abilities,
  encounterRows,
  selectedParticipants,
  encounterAssignmentOverrides,
}: ApplyEventActivityPlanOverridesInput) => {
  if (!encounterAssignmentOverrides) return resolvedByScopeBoxKey
  const rowBySpellId = encounterRows.reduce<
    Record<number, EncounterAbilityRow>
  >((acc, row) => {
    acc[row.spellId] = row
    return acc
  }, {})
  const selectedParticipantByCharacterId = selectedParticipants.reduce<
    Record<number, ResolvedAssignmentParticipant>
  >((acc, participant) => {
    acc[participant.characterId] = participant
    return acc
  }, {})
  const nextResolvedByScopeBoxKey = { ...resolvedByScopeBoxKey }

  abilities.forEach((ability) => {
    const row = rowBySpellId[ability.spell_id]
    if (!row) return
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const viewMode: AssignmentViewMode = getEffectiveAssignmentViewMode(
      ability.view_mode,
    )
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopeIndexes(
      row.entries,
      viewMode,
      customGrouping,
    )

    scopes.forEach((scope) => {
      const castIds = toCastIds(ability, scope.castIndexes, assignmentGroups)
      if (!castIds.length) return
      const representativeCast = normalizeAssignmentCast(
        ability.casts?.[scope.castIndexes[0]],
        assignmentGroups,
      )

      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const key = `${ability.spell_id}:${scope.id}:${boxIndex}`
        const resolvedEntities = nextResolvedByScopeBoxKey[key]
        if (!resolvedEntities?.length) continue
        const box = normalizeAssignmentBox(representativeCast.boxes[boxIndex])

        nextResolvedByScopeBoxKey[key] = resolvedEntities.map(
          (resolvedEntity, entityIndex) => {
            const sourceEntity = box.entities[entityIndex]
            const override = getEncounterAssignmentEntityOverride({
              encounterAssignments: encounterAssignmentOverrides,
              spellId: ability.spell_id,
              castIds,
              boxId: box.id,
              entityId: sourceEntity?.id,
            })
            if (!override) return resolvedEntity
            if (override.kind === 'participant') {
              const participant =
                selectedParticipantByCharacterId[override.characterId]
              if (!participant) return resolvedEntity
              return {
                kind: 'participant',
                participant,
                sourceEntity: resolvedEntity.sourceEntity,
                isOverridden: true,
              }
            }
            return toAggregateOverrideEntity(override)
          },
        )
      }
    })
  })

  return nextResolvedByScopeBoxKey
}

const toAggregateOverrideEntity = (
  override: Extract<EncounterAssignmentEntityOverride, { kind: 'aggregate' }>,
): ResolvedAssignmentEntity => ({
  kind: 'aggregate',
  sourceEntity: override.entity,
  isOverridden: true,
})

export const getEventActivityUnresolvedIssues = (
  resolvedByScopeBoxKey: Record<string, ResolvedAssignmentEntity[]>,
): EventActivityTimelineResolverIssue[] => {
  const issues: EventActivityTimelineResolverIssue[] = []
  Object.values(resolvedByScopeBoxKey).forEach((resolvedEntities) => {
    resolvedEntities.forEach((entity) => {
      if (entity.issue) issues.push(entity.issue)
    })
  })
  return issues
}
